import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import { API } from "@/lib/api";
import { trackPixel, BOOKING_VALUE } from "@/lib/pixel";

let stripePromise = null;
const getStripe = async () => {
  if (!stripePromise) {
    const { data } = await axios.get(`${API}/payments/config`);
    stripePromise = loadStripe(data.publishable_key);
  }
  return stripePromise;
};

const PAID_MSG = "¡Pago recibido! Tu cita quedó confirmada. Te enviamos un correo con los detalles.";

export function usePaymentFlow({ form, onBooked }) {
  const [step, setStep] = useState("form");
  const [loading, setLoading] = useState(false);
  const [oxxoHold, setOxxoHold] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState("");
  const checkoutRef = useRef(null);
  const pollTimerRef = useRef(null);
  const sessionRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/paypal/config`).then(({ data }) => setPaypalClientId(data.client_id || "")).catch((err) => console.warn("paypal config", err));
    return () => {
      clearTimeout(pollTimerRef.current);
      checkoutRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (step === "payment" && checkoutRef.current) checkoutRef.current.mount("#stripe-checkout");
  }, [step]);

  const destroyCheckout = () => {
    clearTimeout(pollTimerRef.current);
    checkoutRef.current?.destroy();
    checkoutRef.current = null;
  };

  const purchase = (eventId, extra = {}) =>
    trackPixel("Purchase", { ...BOOKING_VALUE, content_name: form.servicio, content_type: "product", ...extra }, eventId);

  const completeBooking = (eventId) => {
    destroyCheckout();
    if (sessionRef.current && sessionRef.current !== eventId) {
      axios.post(`${API}/payments/cancel/${sessionRef.current}`).catch(() => {});
    }
    sessionRef.current = null;
    purchase(eventId);
    toast.success(PAID_MSG);
    setOxxoHold(false);
    setStep("form");
    onBooked();
  };

  const handleStatus = (sessionId, data) => {
    if (data.payment_status === "paid" && data.appointment_created) { completeBooking(sessionId); return true; }
    if (data.payment_status === "oxxo_pending" && data.appointment_created) {
      setOxxoHold(true);
      setStep("payment");
      purchase(sessionId, { payment_method: "oxxo" });
      toast.success("¡Tu cita quedó apartada! Paga tu ficha en OXXO antes de 2 días para confirmarla.");
      return true;
    }
    if (["expired", "failed"].includes(data.payment_status)) {
      toast.error("El pago no se completó. Intenta de nuevo.");
      setStep("payment");
      return true;
    }
    return false;
  };

  const pollPayment = (sessionId, attempts = 0) => {
    if (attempts > 100) return;
    const retry = () => { pollTimerRef.current = setTimeout(() => pollPayment(sessionId, attempts + 1), 3000); };
    axios.get(`${API}/payments/status/${sessionId}`)
      .then(({ data }) => { if (!handleStatus(sessionId, data)) retry(); })
      .catch(retry);
  };

  const startCheckout = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, form);
      const stripe = await getStripe();
      checkoutRef.current = await stripe.createEmbeddedCheckoutPage({
        clientSecret: data.client_secret,
        onComplete: () => setStep("processing"),
      });
      sessionRef.current = data.session_id;
      setStep("payment");
      trackPixel("InitiateCheckout", { ...BOOKING_VALUE, content_name: form.servicio, num_items: 1 }, data.session_id);
      pollPayment(data.session_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Ocurrió un error al iniciar el pago. Intenta de nuevo o contáctanos por WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  const cancelPayment = () => {
    destroyCheckout();
    if (oxxoHold) { setOxxoHold(false); onBooked(); }
    else if (sessionRef.current) {
      axios.post(`${API}/payments/cancel/${sessionRef.current}`).catch(() => {});
      sessionRef.current = null;
    }
    setStep("form");
  };

  return { step, loading, oxxoHold, paypalClientId, startCheckout, cancelPayment, completeBooking };
}
